import { useReveal } from '../../../hooks/useReveal';

const benefits = [
  {
    title: 'Exclusive Leads Only',
    description: 'No sharing. No reselling. No competition.',
  },
  {
    title: 'Built for PI Firms',
    description: 'Every campaign is designed specifically for personal injury cases.',
  },
  {
    title: 'Local Market Precision',
    description: 'Targeting your exact city and service area.',
  },
  {
    title: 'Transparent Performance',
    description: 'See every lead, every dollar, every result.',
  },
  {
    title: 'No Long-Term Contracts',
    description: 'Stay because it works \u2014 not because you\'re locked in.',
  },
  {
    title: 'Case-Focused ROI',
    description: 'Optimized for signed cases, not vanity metrics.',
  },
];

export default function BenefitsSection() {
  const { ref: leftRef, isVisible: leftVisible } = useReveal<HTMLDivElement>(0.2);
  const { ref: rightRef, isVisible: rightVisible } = useReveal<HTMLDivElement>(0.15);

  return (
    <section id="benefits" className="flex flex-col md:flex-row">
      {/* Left Panel */}
      <div
        ref={leftRef}
        className="relative w-full md:w-1/2 flex items-center justify-start overflow-hidden min-h-[400px] md:min-h-[600px]"
      >
        {/* Background Image */}
        <img
          src="https://public.readdy.ai/ai/img_res/e87d85924dfc319da7e25662f76a04eb.jpg"
          alt="Law library background"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/65" />
        {/* Content */}
        <div
          className="relative z-10 px-8 md:px-14 py-14 md:py-16 max-w-md"
          style={{
            opacity: leftVisible ? 1 : 0,
            transform: leftVisible ? 'translateX(0)' : 'translateX(-30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Not All Leads Are Created Equal.
          </h2>
          <div className="w-12 h-px bg-[#D4AF37] mb-6" />
          <p
            className="text-white/60 text-base leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We focus on signed cases &mdash;{' '}
            <br />not just clicks, calls, or form fills.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div
        ref={rightRef}
        className="relative w-full md:w-1/2 flex items-center overflow-hidden min-h-[400px] md:min-h-[600px]"
      >
        {/* Background Image - dark rocky stone texture */}
        <img
          src="https://readdy.ai/api/search-image?query=dark%20rough%20stone%20rock%20wall%20texture%20background%2C%20very%20dark%20brown%20and%20black%20tones%2C%20aged%20weathered%20surface%2C%20subtle%20warm%20amber%20and%20rust%20undertones%2C%20dramatic%20moody%20lighting%2C%20abstract%20natural%20stone%20surface%2C%20cinematic%20dark%20atmosphere%2C%20high%20detail%20texture&width=800&height=700&seq=benefits-right-002&orientation=landscape"
          alt="Dark stone texture background"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60" />

        {/* Content */}
        <div
          className="relative z-10 px-8 md:px-14 py-14 md:py-16 w-full"
          style={{
            opacity: rightVisible ? 1 : 0,
            transform: rightVisible ? 'translateX(0)' : 'translateX(30px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          {/* Label */}
          <div className="flex items-center gap-3 mb-10">
            <div className="h-px w-8 bg-[#D4AF37]" />
            <span
              className="text-[#D4AF37] text-[10px] font-semibold tracking-[0.3em] uppercase whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Why PI Firms Choose Ironclad
            </span>
            <div className="h-px w-8 bg-[#D4AF37]" />
          </div>

          {/* Benefit Items */}
          <div className="flex flex-col gap-7">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-4"
                style={{
                  opacity: rightVisible ? 1 : 0,
                  transform: rightVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.5s ease ${0.15 + index * 0.08}s, transform 0.5s ease ${0.15 + index * 0.08}s`,
                }}
              >
                {/* Checkmark */}
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-[#D4AF37] text-lg" />
                </div>
                {/* Text */}
                <div>
                  <h3
                    className="text-white font-bold text-base mb-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {benefit.title}
                  </h3>
                  <p
                    className="text-white/55 text-sm leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
